import axios from "./axiosInstance";
import config from "./config";

const BASE_URL = config.BASE_URL;

// The three staff notices below need a signed-in caller, and the server builds
// the email from its own records: the body only NAMES the record.
//   sendRegistrationEmail({ membershipId })          staff who add memberships
//   sendEnrollmentEmail({ athleteId, slotId })       staff, or the athlete's family
//   sendUnEnrollmentEmail({ athleteId, slotId })     staff, or the athlete's family
// Public registration does not call any of them: completing a registration
// sends the club its "new registration" email on the server.

// POST
export const sendRegistrationEmail = (newForm) => {
  return axios
    .post(`${BASE_URL}/email/send-registration-email`, newForm, {
      headers: {
        "Content-Type": "application/json",      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to send email" };
    });
};

// POST
export const sendEnrollmentEmail = (newForm) => {
  return axios
    .post(`${BASE_URL}/email/send-enrollment-email`, newForm, {
      headers: {
        "Content-Type": "application/json",      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to send email" };
    });
};

// POST
export const sendUnEnrollmentEmail = (newForm) => {
  return axios
    .post(`${BASE_URL}/email/send-unenrollment-email`, newForm, {
      headers: {
        "Content-Type": "application/json",      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to send email" };
    });
};

// POST
export const sendNewInvoiceCreateEmail = (newForm) => {
  return axios
    .post(`${BASE_URL}/email/send-create-invoice-email`, newForm, {
      headers: {
        "Content-Type": "application/json",      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to send email" };
    });
};

// POST
export const sendMarkAttendenceInvoiceCreateEmail = (newForm) => {
  return axios
    .post(`${BASE_URL}/email/send-mark-attendence-invoice-email`, newForm, {
      headers: {
        "Content-Type": "application/json",      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to send email" };
    });
};

// POST
export const sendEmail = (token, newForm) => {
  return axios
    .post(`${BASE_URL}/email/send-email`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,      },
    })
    .then((res) => {
      const body = res?.data;
      // The server answers 200 with `delivery_failed: true` when the message
      // was saved to Message History but the mail service refused it. That
      // is a failed send, not a success. Every caller checks `success` or
      // `status`, so it is turned into a failure here, once, carrying the
      // server's own reason. (2026-10-10: staff saw "Email sent successfully!"
      // next to an entry marked "Not sent".)
      if (body?.delivery_failed) {
        return {
          ...body,
          success: false,
          status: "error",
          error: body.failure_reason || body.message || "The email was not sent.",
        };
      }
      return body;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to send email" };
    });
};

// POST — staff-composed SMS to a single member. Backend gates on the
// member's sms_transactional opt-in and on a usable phone number.
export const sendMemberSms = (token, newForm) => {
  return axios
    .post(`${BASE_URL}/email/send-sms-to-member`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return {
        success: false,
        error: err?.response?.data?.error || err?.message || "Failed to send SMS",
      };
    });
};

// POST
export const sendGroupEmail = (token, newForm) => {
  return axios
    .post(`${BASE_URL}/email/send-group-email`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to send email" };
    });
};

// POST - Preview recipients for group email
export const previewGroupEmailRecipients = (token, payload) => {
  return axios
    .post(`${BASE_URL}/email/preview-recipients`, payload, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to preview recipients" };
    });
};

// POST - Preview audience using condition-based audience builder
export const previewAudienceRecipients = (token, audienceSpec) => {
  return axios
    .post(`${BASE_URL}/email/audience/preview`, { audienceSpec }, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to preview audience" };
    });
};

// POST - "Describe who should get this": a sentence in, the audience
// builder's own filters out, as a proposal. Sends nothing and saves nothing.
// Resolves the BODY like its neighbors, so a failure is flat:
// { success: false, error? }. Only a message the server wrote for staff (a
// sentence that is too short or too long, or too many tries in a minute) is
// passed on; anything else leaves `error` unset and the box says it is not
// available right now.
export const describeAudience = (token, sentence) => {
  return axios
    .post(`${BASE_URL}/email/audience/describe`, { sentence }, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      const status = err?.response?.status;
      const staffMessage = status === 400 || status === 429 ? err?.response?.data?.error : undefined;
      return { success: false, error: staffMessage };
    });
};

// POST
export const sendFeedbackEmail = (token, newForm) => {
  return axios
    .post(`${BASE_URL}/email/send-feedback`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to send email" };
    });
};

// POST
export const sendSupportEmail = (newForm) => {
  return axios
    .post(`${BASE_URL}/email/send-support`, newForm, {
      headers: {
        "Content-Type": "application/json",      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to send email" };
    });
};

export const getMemberMessages = async (memberId, token) => {
  return axios
    .get(`${BASE_URL}/email/messages/${memberId}`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to fetch messages", data: [] };
    });
};

// GET - Preview rendered email for a message
export const getMessagePreview = (token, messageId) => {
  return axios
    .get(`${BASE_URL}/email/messages/${messageId}/preview`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to get preview" };
    });
};

// DEL
export const deleteEmailMessages = (token, messageId) => {
  return axios
    .delete(`${BASE_URL}/email/delete-messages/${messageId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to delete message" };
    });
};

// ============================================
// BROADCAST MANAGEMENT APIs
// ============================================

// GET - Fetch all broadcasts
export const getBroadcasts = async (token, params = {}) => {
  const { status = 'all', limit = 50, offset = 0 } = params;
  const queryParams = new URLSearchParams();
  if (status !== 'all') queryParams.append('status', status);
  queryParams.append('limit', limit.toString());
  queryParams.append('offset', offset.toString());

  return axios
    .get(`${BASE_URL}/email/broadcasts?${queryParams.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to fetch broadcasts", data: [] };
    });
};

// GET - Fetch single broadcast with recipients
export const getBroadcastById = async (token, broadcastId) => {
  return axios
    .get(`${BASE_URL}/email/broadcasts/${broadcastId}`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to fetch broadcast" };
    });
};

// GET - Fetch news broadcasts for member dashboard
export const getNewsBroadcasts = async (token, params = {}) => {
  const { limit = 10, offset = 0 } = params;
  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit.toString());
  queryParams.append('offset', offset.toString());

  return axios
    .get(`${BASE_URL}/email/news?${queryParams.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to fetch news", data: [] };
    });
};

// GET - Fetch broadcast statistics
export const getBroadcastStats = async (token) => {
  return axios
    .get(`${BASE_URL}/email/broadcasts/stats`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to fetch stats", data: { sent: 0, scheduled: 0, drafts: 0, totalRecipients: 0 } };
    });
};

// POST - Save broadcast as draft
export const saveBroadcastDraft = async (token, data) => {
  return axios
    .post(`${BASE_URL}/email/broadcasts/draft`, data, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to save draft" };
    });
};

// PUT - Update broadcast draft
export const updateBroadcastDraft = async (token, broadcastId, data) => {
  return axios
    .put(`${BASE_URL}/email/broadcasts/${broadcastId}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to update draft" };
    });
};

// DELETE - Delete broadcast
export const deleteBroadcast = async (token, broadcastId) => {
  return axios
    .delete(`${BASE_URL}/email/broadcasts/${broadcastId}`, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to delete broadcast" };
    });
};

// POST - Schedule broadcast
export const scheduleBroadcast = async (token, broadcastId, scheduledFor) => {
  return axios
    .post(`${BASE_URL}/email/broadcasts/${broadcastId}/schedule`, { scheduled_for: scheduledFor }, {
      headers: {
        Authorization: `Bearer ${token}`,      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to schedule broadcast" };
    });
};
// GET - Scheduled broadcasts: what will go out by itself, and what is past
// its time and needs a person
export const listScheduledBroadcasts = async (token) => {
  return axios
    .get(`${BASE_URL}/email/broadcasts/scheduled`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to load scheduled broadcasts" };
    });
};

// POST - Send a scheduled broadcast now
export const sendScheduledBroadcastNow = async (token, broadcastId, clubName) => {
  return axios
    .post(`${BASE_URL}/email/broadcasts/${broadcastId}/send-now`, { clubName }, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to send broadcast" };
    });
};

// POST - Finish a broadcast that stopped part-way: email only the people it
// never reached
export const sendRemainingBroadcast = async (token, broadcastId) => {
  return axios
    .post(`${BASE_URL}/email/broadcasts/${broadcastId}/send-remaining`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to finish sending this broadcast" };
    });
};

// POST - Close a broadcast that stopped part-way, as it stands (sends nothing)
export const markBroadcastFinished = async (token, broadcastId) => {
  return axios
    .post(`${BASE_URL}/email/broadcasts/${broadcastId}/mark-finished`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to mark this broadcast as finished" };
    });
};

// POST - Take a broadcast off the schedule (it goes back to Drafts, unsent)
export const cancelScheduledBroadcast = async (token, broadcastId) => {
  return axios
    .post(`${BASE_URL}/email/broadcasts/${broadcastId}/cancel-schedule`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => res?.data)
    .catch((err) => {
      return { success: false, error: err?.response?.data?.error || err?.message || "Failed to cancel the schedule" };
    });
};
